import { FlyerCard, LiveFeedback, LoadingSpinner } from '@/components';
import { FlyerMasonryInfinite } from '@/components/FlyerboardPagination/FlyerMasonryInfinite';
import { FlyerType } from '@/config/constants';
import { GET_ORG, GET_ORG_PROFILE_VISIBILITY_SETTINGS } from '@/graphql/queries';
import {
  AllFlyerFilters,
  GetOrgProfileVisibilitySettingsResponse,
  GetOrgResponse,
} from '@/graphql/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useExpandedCommunityFlyerboardPagination } from '@/hooks/useExpandedCommunityFlyerboardPagination';
import { useChildGrades, useLiveFeedback, useSchools } from '@/hooks';
import { FlyerStatus } from '@/types/flyer';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import scrollToTop from '@/utils/scroolToTop';
import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TitleSmallEmphasized } from '@/design-system';
import { useJarFlyerboardContext } from '@/context/JarFlyerboardContext';
import styles from './OrgProfile.styles';
import OrgProfileDrawer from './components/OrgProfileDrawer/OrgProfileDrawer';
import OrgProfileHeader from './components/OrgProfileHeader/OrgProfileHeader';
import { OrgProfileInfo } from './components/OrgProfileInfo/OrgProfileInfo';

export const OrgProfile: React.FC = () => {
  const { id } = useParams();
  const { data, loading: loadingOrg } = useQuery<GetOrgResponse>(GET_ORG, {
    skip: !id,
    variables: {
      orgId: Number(id),
    },
  });
  const { data: visibilityData, loading: loadingVisibility } =
    useQuery<GetOrgProfileVisibilitySettingsResponse>(GET_ORG_PROFILE_VISIBILITY_SETTINGS, {
      skip: !id,
      variables: {
        orgId: Number(id),
      },
    });
  const [showOptions, setShowOptions] = useState(false);
  const { track } = useAnalytics();
  const { filters, districtId } = useJarFlyerboardContext();
  const { grades } = useChildGrades();
  const { subscriptionsIds } = useSchools();
  const trackId = 'org_profile_page';

  const org = data?.org;
  const visibilitySettings = visibilityData?.getOrgProfileVisibilitySettings;

  const activeFlyersFilters = useMemo<AllFlyerFilters>(
    () => ({
      ...filters,
      grades: grades.map(grade => grade.id),
      schoolIds: subscriptionsIds,
      includeExpandedCommunity: true,
    }),
    [filters, grades, subscriptionsIds]
  );

  const expiredFlyersFilters = useMemo<AllFlyerFilters>(
    () => ({
      ...activeFlyersFilters,
      isExpired: true,
      includeExpandedCommunity: false,
    }),
    [activeFlyersFilters]
  );

  const {
    flyers: activeFlyers,
    loading: loadingActiveFlyers,
    fetchMoreFlyers: fetchMoreActiveFlyers,
    total: activeFlyersTotal,
    isPaginationEnd,
  } = useExpandedCommunityFlyerboardPagination({
    flyerType: FlyerType.community,
    flyerboardFilters: activeFlyersFilters,
    districtId,
    owningIds: org?.orgId ? [org.orgId] : undefined,
    skip: !org?.orgId,
    shouldUpdateFacets: false,
  });

  const {
    flyers: expiredFlyers,
    loading: loadingExpiredFlyers,
    fetchMoreFlyers: fetchMoreExpiredFlyers,
    total: expiredFlyersTotal,
  } = useExpandedCommunityFlyerboardPagination({
    flyerType: FlyerType.community,
    flyerboardFilters: expiredFlyersFilters,
    districtId,
    owningIds: org?.orgId ? [org.orgId] : undefined,
    skip: !isPaginationEnd,
    shouldUpdateFacets: false,
    sortBy: 'endDate',
  });

  const loading = loadingActiveFlyers || loadingExpiredFlyers;

  const orgImage = activeFlyers[0]?.frontImageUrl ?? expiredFlyers[0]?.frontImageUrl ?? '';
  const isImageLoading = !orgImage && loading;

  const isSingleActiveFlyer = activeFlyers.length === 1;

  const [activeFeedback, announceActive] = useLiveFeedback({ delay: 2000 });
  const [expiredFeedback, announceExpired] = useLiveFeedback({ delay: 2000 });

  const wasLoadingActiveRef = useRef(false);
  const wasLoadingExpiredRef = useRef(false);

  useEffect(() => {
    if (wasLoadingActiveRef.current && !loadingActiveFlyers) {
      announceActive(activeFlyers.length > 0 ? 'Current flyers loaded' : 'No current flyers found');
    }
    wasLoadingActiveRef.current = loadingActiveFlyers;
  }, [loadingActiveFlyers, activeFlyers.length]);

  useEffect(() => {
    if (wasLoadingExpiredRef.current && !loadingExpiredFlyers) {
      announceExpired(
        expiredFlyers.length > 0 ? 'Expired flyers loaded' : 'No expired flyers found'
      );
    }
    wasLoadingExpiredRef.current = loadingExpiredFlyers;
  }, [loadingExpiredFlyers, expiredFlyers.length]);

  useEffect(() => {
    scrollToTop();
  }, []);

  if (!org || !visibilitySettings) {
    return loadingOrg || loadingVisibility ? <LoadingSpinner /> : null;
  }

  const toggleDrawer = (open: boolean) => (event: any) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:show_options`,
      label: org.name,
      value: Number(org.orgId),
    });
    setShowOptions(open);
  };

  const trackButtonClick = (label: string) => {
    track({
      category: trackId,
      action: GenericTrackTypes.BUTTON_PRESSED,
      label,
    });
  };

  return (
    <>
      <OrgProfileHeader
        orgName={org.name}
        image={orgImage}
        isImageLoading={isImageLoading}
        trackButtonClick={trackButtonClick}
        toggleDrawer={toggleDrawer}
      />
      <main>
        <OrgProfileInfo
          org={org}
          visibilitySettings={visibilitySettings}
          trackButtonClick={trackButtonClick}
        />
        <div aria-busy={loading} role="region" aria-label="Flyers">
          <LiveFeedback message={activeFeedback} />
          <LiveFeedback message={expiredFeedback} />
          {loading && activeFlyers.length === 0 && expiredFlyers.length === 0 && <LoadingSpinner />}

          {activeFlyers.length > 0 && (
            <>
              <div className={styles.flyerSection} />
              <div className={styles.section}>
                <TitleSmallEmphasized className={styles.sectionTitle} as="h2">
                  {`Current Flyer${activeFlyersTotal > 1 ? 's' : ''}`}
                </TitleSmallEmphasized>
                <div className={styles.flyersContainer}>
                  <FlyerMasonryInfinite
                    style={{ overflow: 'hidden' }}
                    items={activeFlyers}
                    total={activeFlyersTotal}
                    nextPage={fetchMoreActiveFlyers}
                    breakpointCols={isSingleActiveFlyer ? { default: 1 } : undefined}
                    renderItem={item => (
                      <FlyerCard
                        key={item.flyerId}
                        id={item.flyerId}
                        title={item.title}
                        thumbnail={
                          isSingleActiveFlyer && item.pages.length > 0
                            ? item.pages[0].imageUrl
                            : item.frontImageUrl
                        }
                        isFavorite={item.isFavorite}
                        inactive={item.status === FlyerStatus.expired}
                        status={item.status}
                        flyerType={item.type}
                        trackId={`${trackId}:current_program`}
                        trackingEventFrom=""
                        distributionsReleased={item.distributionsReleased}
                        isRead={item.isRead}
                        isNew={item.isNew}
                        showNewBadge
                        ctas={item.ctas}
                        pages={item.pages}
                        linkState={{ fromOrgProfile: true }}
                      />
                    )}
                  />
                </div>
              </div>
            </>
          )}

          {expiredFlyers.length > 0 && (
            <>
              <div className={styles.flyerSection} />
              <div className={styles.section}>
                <TitleSmallEmphasized className={styles.sectionTitle} as="h2">
                  Expired Flyers
                </TitleSmallEmphasized>
                <div className={styles.flyersContainer}>
                  <FlyerMasonryInfinite
                    style={{ overflow: 'hidden' }}
                    items={expiredFlyers}
                    total={expiredFlyersTotal}
                    nextPage={fetchMoreExpiredFlyers}
                    renderItem={item => (
                      <FlyerCard
                        key={item.flyerId}
                        id={item.flyerId}
                        title={item.title}
                        thumbnail={item.frontImageUrl}
                        isFavorite={item.isFavorite}
                        inactive
                        status={item.status}
                        flyerType={item.type}
                        trackId={`${trackId}:previouslyPosted`}
                        trackingEventFrom=""
                        distributionsReleased={item.distributionsReleased}
                        isRead={item.isRead}
                        ctas={item.ctas}
                        pages={item.pages}
                        linkState={{ fromOrgProfile: true }}
                      />
                    )}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <OrgProfileDrawer setShowOptions={setShowOptions} showOptions={showOptions} />
    </>
  );
};
